import { A } from '@adornis/base/env-info.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import '@adornis/file-utils/client/x-image.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import * as m from '../../../../localization/out/messages.js';
import { subscribeJobApplicationsByUser } from '../../../api/job-application.js';
import { getAllJobPositions } from '../../../api/job-position.js';
import { subscribeMatchingWeekSettings } from '../../../api/settings.js';
import {
  EmploymentType,
  JobPosition,
  PreferredStudyField,
  SELECTABLE_SPEECH_LEVELS,
} from '../../../db/job-position.js';
import { createJobSearchFilter } from '../../../shared/job-search.js';
import { UserToolbar } from '../../components/user-toolbar.js';
import { translateEnum } from '../../translate-enum.js';
import { JobCard } from './job-card.js';
import { registerRouter } from './register-routes.js';

// Helper function for language level filtering
const createLanguageLevelFilter = (fieldName: string, val: string, filter: AdornisFilter): AdornisFilter => {
  const next = new AdornisFilter({ ...filter.toJSON() });

  if (!val) {
    next.removeFilterAspect(fieldName);
    return next;
  }

  // Define the language level order from lowest to highest
  // NONE and UNKNOWN are special cases - we'll handle them separately
  const levelOrder = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
  const selectedIndex = levelOrder.indexOf(val);

  if (selectedIndex >= 0) {
    // Include only this level and lower levels (not higher levels)
    const includedLevels = levelOrder.slice(0, selectedIndex + 1);

    // Special handling for NONE (no language requirement)
    if (val !== 'NONE') {
      includedLevels.push('NONE');
    }

    // Use containsExactOR mode which will use $in operator in MongoDB
    next.setFilterAspect(
      new StringAspect({
        fieldName,
        mode: 'containsExactOR',
        value: includedLevels.join(','),
      }),
    );
  } else if (val === 'NONE') {
    // If NONE is selected, only show jobs with NONE
    next.setFilterAspect(new StringAspect({ fieldName, mode: 'equals', value: 'NONE' }));
  } else if (val === 'UNKNOWN') {
    // If UNKNOWN is selected, only show jobs with UNKNOWN
    next.setFilterAspect(new StringAspect({ fieldName, mode: 'equals', value: 'UNKNOWN' }));
  } else {
    // Fallback to equals if the level is not recognized
    next.setFilterAspect(new StringAspect({ fieldName, mode: 'equals', value: val }));
  }

  return next;
};

const FiltersBar = stateful(
  ({
    filter,
    onFilterChanged,
    onClearAll,
  }: {
    filter: AdornisFilter;
    onFilterChanged: (filter: AdornisFilter) => void;
    onClearAll: () => void;
  }) => {
    const [search, setSearch] = useState(filter.search ?? '', [filter]);
    const [debounce] = useState<{ handle?: number }>({}, []);

    const cancelSearchDebounce = () => {
      window.clearTimeout(debounce.handle);
      debounce.handle = undefined;
    };

    // Discard pending searches when another filter is applied or the board is unmounted.
    useEffect(() => cancelSearchDebounce, [filter]);

    const applySearch = (value: string) => {
      cancelSearchDebounce();
      onFilterChanged(createJobSearchFilter(value, filter));
    };

    const onSearchInput = (e: InputEvent) => {
      const val = (e.currentTarget as HTMLInputElement).value;
      cancelSearchDebounce();
      setSearch(val);
      debounce.handle = window.setTimeout(() => applySearch(val), 200);
    };

    const onSearchKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.isComposing) return;
      e.preventDefault();
      applySearch((e.currentTarget as HTMLInputElement).value);
    };

    const clearSearch = () => {
      setSearch('');
      applySearch('');
    };

    const onStudyFieldChanged = (e: Event) => {
      const val = (e.target as HTMLSelectElement).value;
      const next = new AdornisFilter({ ...filter.toJSON() });
      if (!val) next.removeFilterAspect('preferredStudyField');
      else next.setFilterAspect(new StringAspect({ fieldName: 'preferredStudyField', mode: 'equals', value: val }));
      onFilterChanged(next);
    };

    const onEmploymentTypeChanged = (e: Event) => {
      const val = (e.target as HTMLSelectElement).value;
      const next = new AdornisFilter({ ...filter.toJSON() });
      if (!val) next.removeFilterAspect('employmentType');
      else next.setFilterAspect(new StringAspect({ fieldName: 'employmentType', mode: 'equals', value: val }));
      onFilterChanged(next);
    };

    const clearStudyField = () => {
      const next = new AdornisFilter({ ...filter.toJSON() });
      next.removeFilterAspect('preferredStudyField');
      onFilterChanged(next);
    };

    const clearEmploymentType = () => {
      const next = new AdornisFilter({ ...filter.toJSON() });
      next.removeFilterAspect('employmentType');
      onFilterChanged(next);
    };

    const onGermanLevelChanged = (e: Event) => {
      const val = (e.target as HTMLSelectElement).value;
      const next = createLanguageLevelFilter('languageRequirementsDE', val, filter);
      onFilterChanged(next);
    };

    const onEnglishLevelChanged = (e: Event) => {
      const val = (e.target as HTMLSelectElement).value;
      const next = createLanguageLevelFilter('languageRequirementsEN', val, filter);
      onFilterChanged(next);
    };

    const clearGermanLevel = () => {
      const next = new AdornisFilter({ ...filter.toJSON() });
      next.removeFilterAspect('languageRequirementsDE');
      onFilterChanged(next);
    };

    const clearEnglishLevel = () => {
      const next = new AdornisFilter({ ...filter.toJSON() });
      next.removeFilterAspect('languageRequirementsEN');
      onFilterChanged(next);
    };

    // Derive current values from filter for select bindings
    const studyFieldVal = (filter as any).getFilterAspect('preferredStudyField')?.value || '';
    const employmentTypeVal = (filter as any).getFilterAspect('employmentType')?.value || '';
    const deAspect: any = (filter as any).getFilterAspect('languageRequirementsDE') || undefined;
    const enAspect: any = (filter as any).getFilterAspect('languageRequirementsEN') || undefined;
    const germanLevel = typeof deAspect?.value === 'string' ? (deAspect.value as string) : '';
    const englishLevel = typeof enAspect?.value === 'string' ? (enAspect.value as string) : '';

    return html`
      <div class="w-full">
        <div>
          <div class="flex flex-wrap items-end gap-2">
            <label class="input input-bordered flex-1 min-w-[16rem] relative">
              <input
                type="text"
                placeholder="${m.search_placeholder()}"
                .value=${search}
                @input=${onSearchInput}
                @keydown=${onSearchKeyDown}
              />
              ${
                search
                  ? html`<button
                      type="button"
                      class="btn btn-xs btn-circle btn-ghost absolute right-2 top-1/2 -translate-y-1/2"
                      title="${m.search_clear_title()}"
                      @click=${clearSearch}
                    >
                      ✕
                    </button>`
                  : html``
              }
            </label>

            <label class="form-control min-w-[12rem] w-full sm:w-auto sm:mt-0">
              <div class="label py-0"><span class="label-text text-xs">${m.study_field()}</span></div>
              <div class="flex items-center gap-2 w-full">
                <select class="select select-bordered w-full" .value=${studyFieldVal} @change=${onStudyFieldChanged}>
                  <option value="">${m.please_choose()}</option>
                  ${Object.entries(PreferredStudyField)
                    .filter(([key]) => !['_class', '_isBaseQLEnum'].includes(key))
                    .map(
                      ([key, value]) => html`<option value=${value}>${translateEnum('job_form_enum', value)}</option>`,
                    )}
                </select>
                ${
                  studyFieldVal
                    ? html`<button
                        class="btn btn-xs btn-ghost"
                        title="${m.reset_study_field_title()}"
                        @click=${clearStudyField}
                      >
                        ✕
                      </button>`
                    : html``
                }
              </div>
            </label>

            <label class="form-control min-w-[12rem] w-full sm:w-auto sm:mt-0">
              <div class="label py-0"><span class="label-text text-xs">${m.job_form_type_label()}</span></div>
              <div class="flex items-center gap-2 w-full">
                <select
                  class="select select-bordered w-full"
                  .value=${employmentTypeVal}
                  @change=${onEmploymentTypeChanged}
                >
                  <option value="">${m.please_choose()}</option>
                  <option value=${EmploymentType.INTERNSHIP}>
                    ${translateEnum('job_form_enum', EmploymentType.INTERNSHIP)}
                  </option>
                  <option value=${EmploymentType.WORKINGSTUDENT}>
                    ${translateEnum('job_form_enum', EmploymentType.WORKINGSTUDENT)}
                  </option>
                  <option value=${EmploymentType.TRAINEE}>
                    ${translateEnum('job_form_enum', EmploymentType.TRAINEE)}
                  </option>
                  <option value=${EmploymentType.FULLTIME}>
                    ${translateEnum('job_form_enum', EmploymentType.FULLTIME)}
                  </option>
                  <option value=${EmploymentType.THESIS}>
                    ${translateEnum('job_form_enum', EmploymentType.THESIS)}
                  </option>
                </select>
                ${
                  employmentTypeVal
                    ? html`<button
                        class="btn btn-xs btn-ghost"
                        title="${m.reset_employment_type_title()}"
                        @click=${clearEmploymentType}
                      >
                        ✕
                      </button>`
                    : html``
                }
              </div>
            </label>
          </div>

          <div class="mt-2 flex flex-col gap-2">
            <div class="flex flex-wrap gap-2 justify-end">
              <div class="w-full sm:w-auto">
                <div class="label py-0"><span class="label-text text-xs">${m.german_level_label()}</span></div>
                <div class="flex items-center gap-2">
                  <div class="w-full sm:w-64">
                    <select class="select select-bordered w-full" @change=${onGermanLevelChanged}>
                      <option value="" ?selected=${!germanLevel}>${m.level_placeholder()}</option>
                      ${Object.values(SELECTABLE_SPEECH_LEVELS).map(
                        value =>
                          html`<option value=${value} ?selected=${value === germanLevel}>
                            ${translateEnum('job_form_enum', value)}
                          </option>`,
                      )}
                    </select>
                  </div>
                  ${
                    germanLevel
                      ? html`<button
                          class="btn btn-xs btn-ghost"
                          title="${m.reset_german_level_title()}"
                          @click=${clearGermanLevel}
                        >
                          ✕
                        </button>`
                      : html``
                  }
                </div>
              </div>

              <div class="w-full sm:w-auto">
                <div class="label py-0"><span class="label-text text-xs">${m.english_level_label()}</span></div>
                <div class="flex items-center gap-2">
                  <div class="w-full sm:w-64">
                    <select class="select select-bordered w-full" @change=${onEnglishLevelChanged}>
                      <option value="" ?selected=${!englishLevel}>${m.level_placeholder()}</option>
                      ${Object.values(SELECTABLE_SPEECH_LEVELS).map(
                        value =>
                          html`<option value=${value} ?selected=${value === englishLevel}>
                            ${translateEnum('job_form_enum', value)}
                          </option>`,
                      )}
                    </select>
                  </div>
                  ${
                    englishLevel
                      ? html`<button
                          class="btn btn-xs btn-ghost"
                          title="${m.reset_english_level_title()}"
                          @click=${clearEnglishLevel}
                        >
                          ✕
                        </button>`
                      : html``
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  },
);

export const RegisterDashboard = stateful(() => {
  const [filter, setFilter] = useState(createJobSearchFilter(), []);
  const settingsQuery = useQuery(() => subscribeMatchingWeekSettings()({ applicationPhaseClosed: 1 }), []);
  const applicationPhaseClosed = settingsQuery.value?.applicationPhaseClosed !== false;
  const settingsLoaded = !settingsQuery.loading && settingsQuery.value !== undefined;

  // Clear all filters function for the entire dashboard
  const clearAll = () => {
    setFilter(createJobSearchFilter());
  };
  const [selected, setSelected] = useState<Set<string>>(new Set(), []);
  const [vizOpen, setVizOpen] = useState(false, []);
  const userObs = useObservable(() => currentUser<User>(), []);
  const applications = useQuery(() => {
    if (!userObs?._id) return of([]);
    return subscribeJobApplicationsByUser(userObs._id)({ jobPositionId: 1 });
  }, [userObs?._id]);
  const jobs = useQuery(
    () =>
      getAllJobPositions(filter)({
        _id: 1,
        title: 1,
        location: 1,
        employmentType: 1,
        // description: 1,
        companyID: 1,
        categoryID: 1,
        languageRequirementsDE: 1,
        languageRequirementsEN: 1,
        expectedAcademicTitle: 1,
        category: { name: 1 },
        company: { name: 1, logoID: 1 },
      }),
    [filter],
  );

  const toggleSelect = (id: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    setSelected(next);
  };

  const clearSelection = () => setSelected(new Set());

  const appliedIds = new Set<string>(
    ((applications.value ?? []) as Array<{ jobPositionId?: string | null }>)
      .map(a => a.jobPositionId!)
      .filter((v): v is string => Boolean(v)),
  );

  const selectableSelected = Array.from(selected).filter(id => !appliedIds.has(id));

  const jobCategories = (category: string, jobs: JobPosition[]) => {
    return html`
      <section>
        <div class="collapse collapse-plus rounded-box border border-accent overflow-hidden">
          <input type="checkbox" checked />
          <div class="collapse-title text-lg font-semibold bg-accent text-accent-content">${category}</div>
          <div class="collapse-content bg-base-100 p-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              ${jobs.map(job =>
                JobCard({
                  job,
                  isSelected: selected.has(job._id) && !appliedIds.has(job._id),
                  onToggle: toggleSelect,
                  cannotApply: appliedIds.has(job._id) || applicationPhaseClosed,
                  hideSelector: applicationPhaseClosed,
                }),
              )}
            </div>
          </div>
        </div>
      </section>
    `;
  };

  // group jobs by categoryID for rendering
  const groupedByCategory = (jobs.value ?? []).reduce(
    (acc, job) => {
      const key = job.category?.name ?? m.no_category();
      (acc[key] = acc[key] ?? []).push(job);
      return acc;
    },
    {} as Record<string, JobPosition[]>,
  );

  return html`
    <div class="flex flex-col h-full">
      <div class="overflow-y-auto">
        <div class="p-4 sm:p-8">
          <div class="max-w-7xl mx-auto space-y-6">
            <div class="flex justify-end items-center gap-2">
              ${
                userObs
                  ? html`<a
                      href="/candidate"
                      class="btn btn-ghost btn-square"
                      title="${m.candidate_profile_title_application_overview()}"
                    >
                      <span class="icon-[material-symbols--person] w-6 h-6"></span>
                    </a>`
                  : nothing
              }
              ${UserToolbar()}
            </div>

            ${
              settingsLoaded && applicationPhaseClosed
                ? html`
                    <div class="alert alert-warning shadow-lg">
                      <div>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="stroke-current flex-shrink-0 h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        <div>
                          <h3 class="font-bold">${m.registration_closed_title()}</h3>
                          <div class="text-sm" .innerHTML=${m.registration_closed_message()}></div>
                        </div>
                      </div>
                    </div>
                  `
                : nothing
            }
            ${FiltersBar({
              filter,
              onFilterChanged: (filter: AdornisFilter) => {
                setFilter(filter);
              },
              onClearAll: clearAll,
            })}
            <div class="flex flex-wrap justify-end items-center gap-4 sticky top-2 z-10">
              <button class="btn btn-ghost" @click=${clearAll}>${m.clear_all_filters()}</button>

              ${
                !applicationPhaseClosed && selectableSelected.length > 0
                  ? html`
                      <div>
                        <button class="btn btn-accent shadow-lg" @click=${() => setVizOpen(true)}>
                          ${m.selected_button()}
                          <div class="badge badge-tertiary bg-tertiary border-tertiary ms-2">
                            ${selectableSelected.length}
                          </div>
                        </button>
                      </div>
                    `
                  : nothing
              }
              ${
                applicationPhaseClosed
                  ? html`<button class="btn btn-primary btn-disabled shadow-lg" disabled>${m.apply_now()}</button>`
                  : html`
                      <a
                        class="btn btn-primary shadow-lg ${selectableSelected.length ? '' : 'btn-disabled'}"
                        aria-disabled=${selectableSelected.length ? 'false' : 'true'}
                        href=${
                          selectableSelected.length
                            ? registerRouter.link('register', { searchParams: { jobs: selectableSelected.join(',') } })
                            : nothing
                        }
                      >
                        ${m.apply_now()}
                      </a>
                    `
              }
            </div>
            <div class="flex flex-col gap-4">
              ${
                Object.keys(groupedByCategory).length === 0
                  ? html`
                      <div class="flex flex-col items-center justify-center py-16 text-center">
                        <div class="text-6xl mb-4 text-base-content/30">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-24 w-24"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="1.5"
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>
                        <h3 class="text-2xl font-semibold mb-2">${m.no_results_found()}</h3>
                        <p class="text-base-content/70 max-w-md">
                          ${
                            filter.search
                              ? html`${m.no_results_adjust_search()}`
                              : html`${m.no_results_no_matching_jobs()}`
                          }
                        </p>
                        <button class="btn btn-primary mt-6" @click=${() => clearAll()}>
                          ${m.clear_all_filters()}
                        </button>
                      </div>
                    `
                  : html`
                      ${Object.entries(groupedByCategory)
                        .sort((a, b) => {
                          const ak = a[0];
                          const bk = b[0];
                          const none = m.no_category();
                          if (ak === none && bk !== none) return 1; // push "Ohne Kategorie" to the end
                          if (bk === none && ak !== none) return -1;
                          return ak.localeCompare(bk);
                        })
                        .map(([category, list]) => jobCategories(category, list))}
                    `
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Footer with legal links -->
      <div class="mt-8 py-4 border-t border-base-300">
        <div class="flex justify-center gap-4 text-sm text-base-content/70">
          <a href="${A.absoluteUrl('/privacy')}" class="hover:text-base-content transition-colors">
            ${m.privacy_policy()}
          </a>
          <a href="${A.absoluteUrl('/imprint')}" class="hover:text-base-content transition-colors"> ${m.imprint()} </a>
        </div>
      </div>

      <!-- Visualization Modal -->
      ${
        vizOpen
          ? html`
              <div class="modal modal-open">
                <div class="modal-box max-w-5xl">
                  <h3 class="font-bold text-lg">${m.selected_job_ads_title()}</h3>
                  ${
                    selectableSelected.length === 0
                      ? html`<p class="py-4">${m.no_jobs_selected()}</p>`
                      : html`
                          <div class="py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            ${((jobs.value ?? []).filter(j => selectableSelected.includes(j._id)) as JobPosition[]).map(
                              job => JobCard({ job, isSelected: true, onToggle: toggleSelect, hideSelector: true }),
                            )}
                          </div>
                        `
                  }
                  <div class="modal-action">
                    ${
                      selectableSelected.length > 0
                        ? html`<button class="btn btn-ghost" @click=${clearSelection}>${m.clear_selection()}</button>`
                        : html``
                    }
                    <button class="btn" @click=${() => setVizOpen(false)}>${m.close()}</button>
                  </div>
                </div>
              </div>
            `
          : html``
      }
    </div>
  `;
});
